import React from 'react'
import { rate } from '../data/results'

export const plural = (n, one, many) => `${Number(n || 0).toLocaleString('en-GB')} ${n === 1 ? one : many}`
export const moneyOf = (currency = '£') => (n) => `${currency}${Math.round(Number(n) || 0).toLocaleString('en-GB')}`
export const pctText = v => (v == null ? '·' : `${Number.isInteger(v) ? v : v.toFixed(1)}%`)
// "a, b and c"
export const listOf = items => items.join(', ').replace(/, ([^,]*)$/, ' and $1')

// The colours each source wears, in the bar, the chart and the legend
const SOURCE_STYLE = {
  campaigns: 'pf-c-camp',
  sends: 'pf-c-send',
  frontdesk: 'pf-c-fd',
  journeys: 'pf-c-jour',
}

// Up or down on the period before; nothing when there's nothing to compare
export const Delta = ({ now, prev, format = v => Number(v).toLocaleString('en-GB'), on = false }) => {
  if (now == null || prev == null || (!now && !prev)) return null
  const diff = now - prev
  if (!diff) return <span className={`pf-delta${on ? ' is-on' : ''}`}>Same as the period before</span>
  return <span className={`pf-delta ${diff > 0 ? 'is-up' : 'is-down'}${on ? ' is-on' : ''}`}>{diff > 0 ? '▲' : '▼'} {format(Math.abs(diff))} on the period before</span>
}

// A headline number. `value` undefined: still reading; null: can't be counted yet
export const Kpi = ({ label, value, sub, delta, lead = false, pending }) => (
  <div className={`pf-kpi${lead ? ' is-lead' : ''}`}>
    <span className="pf-kpi-label">{label}</span>
    {value === undefined ? <span className="pf-skel" aria-label="Loading" />
      : value === null ? <span className="pf-kpi-pending">{pending || 'Coming soon'}</span>
        : <b className="pf-kpi-num">{value}</b>}
    {value != null && delta}
    {value != null && sub ? <span className="pf-kpi-sub">{sub}</span> : null}
  </div>
)

export const Card = ({ title, aside, children, className = '' }) => (
  <section className={`pf-card ${className}`}>
    {(title || aside) && <div className="pf-card-head">{title && <h3 className="pf-card-title">{title}</h3>}{aside}</div>}
    {children}
  </section>
)

// Something that can't be counted yet, said plainly
export const Pending = ({ children, action }) => (
  <div className="pf-pending">
    <p>{children}</p>
    {action}
  </div>
)

export const Empty = ({ title, children, action }) => (
  <div className="pf-empty">
    <b>{title}</b>
    {children ? <p>{children}</p> : null}
    {action}
  </div>
)

// Where bookings came from, as one bar and a legend
export const SourceBar = ({ parts }) => {
  const total = parts.reduce((n, p) => n + p.value, 0)
  return (
    <div className="pf-sources">
      <div className="pf-bar" role="img" aria-label={parts.map(p => `${p.label} ${p.value}`).join(', ')}>
        {total ? parts.filter(p => p.value).map(p => <span key={p.id} className={SOURCE_STYLE[p.id]} style={{ width: `${(p.value / total) * 100}%` }} />) : <span className="pf-bar-empty" />}
      </div>
      <div className="pf-legend">
        {parts.map(p => <span key={p.id}><i className={SOURCE_STYLE[p.id]} />{p.label} <b>{p.value.toLocaleString('en-GB')}</b></span>)}
      </div>
    </div>
  )
}

// Bookings by week (or month), stacked by source, drawn to one scale
export const WeekChart = ({ buckets, keys }) => {
  const W = 520
  const H = 210
  const top = 20
  const bottom = 180
  const left = 40
  const right = 510
  const max = Math.max(4, ...buckets.map(b => b.total))
  const step = Math.ceil(max / 4)
  const ceiling = step * 4
  const scale = v => ((bottom - top) * v) / ceiling
  const slot = (right - left) / Math.max(1, buckets.length)
  const bar = Math.min(60, slot * 0.56)
  // Past a year of months, every other label, always ending on the latest
  const every = Math.ceil(buckets.length / 13)
  return (
    <svg className="pf-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Bookings: ${buckets.map(b => `${b.label} ${b.total}`).join(', ')}`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const y = bottom - scale(step * i)
        return (
          <g key={i}>
            <line x1={left} y1={y} x2={right} y2={y} />
            <text x={left - 8} y={y + 4} textAnchor="end">{step * i}</text>
          </g>
        )
      })}
      {buckets.map((b, i) => {
        const x = left + slot * i + (slot - bar) / 2
        let y = bottom
        return (
          <g key={b.from}>
            {keys.map((k) => {
              const h = scale(b[k] || 0)
              if (!h) return null
              y -= h
              return <rect key={k} x={x} y={y} width={bar} height={h} className={SOURCE_STYLE[k]} />
            })}
            {b.total > 0 && <text className="pf-chart-total" x={x + bar / 2} y={y - 6} textAnchor="middle">{b.total}</text>}
            {(buckets.length - 1 - i) % every === 0 && <text x={x + bar / 2} y={H - 10} textAnchor="middle">{b.label}</text>}
          </g>
        )
      })}
    </svg>
  )
}

// One step of a funnel, as a share of the first (`first` is that step)
export const FunnelRow = ({ label, value, base, first = false, quiet = false }) => {
  const share = rate(value || 0, base)
  return (
    <div className={`pf-fr${quiet ? ' is-quiet' : ''}`}>
      <span>{label}</span>
      <div className="pf-fr-track"><span style={{ width: `${share == null ? 0 : Math.max(value ? 2 : 0, share)}%` }} /></div>
      <b>{Number(value || 0).toLocaleString('en-GB')}{!first && value && share != null ? <em> {pctText(share)}</em> : null}</b>
    </div>
  )
}
